import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Home, Film, Plus, PlaySquare, User, UserPlus, LogIn, Smartphone, Radio, MessageCircle, Search, Bell, Settings, Menu } from 'lucide-react';
import { QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';
import { TabType, UserProfile, MediaHistoryItem, AppUser } from '../types';
import { BrandLogo } from '../features/shared/components/BrandLogo';
import { AppDrawer } from './drawer/AppDrawer';
import { FollowButton } from '../features/shared/components/FollowButton';
import { HomeTab } from '../features/posts/HomeTab';
import { ShortsTab } from '../features/shorts/ShortsTab';
import { LongsTab } from '../features/longs/LongsTab';
import { ProfileTab } from '../features/profile/ProfileTab';

const LiveStreamScreen = lazy(() => import('../features/live/LiveStreamScreen').then(m => ({ default: m.LiveStreamScreen })));
const CreateModal = lazy(() => import('../features/create/CreateModal').then(m => ({ default: m.CreateModal })));
const ChatScreen = lazy(() => import('../features/chat/ChatScreen').then(m => ({ default: m.ChatScreen })));
const AppPermissionsModal = lazy(() => import('../features/shared/components/AppPermissionsModal').then(m => ({ default: m.AppPermissionsModal })));
const NotificationSettingsModal = lazy(() => import('../features/shared/components/NotificationSettingsModal').then(m => ({ default: m.NotificationSettingsModal })));
import {
  AppNotification,
  enablePushNotifications,
  loadNotificationPage,
  loadNotificationSettings,
  markAllNotificationsRead,
  NotificationCounters,
  NotificationPage,
  saveNotificationSettings,
  subscribeToNotificationCounters
} from '../backend/notificationService';
import { searchUsers } from '../backend/searchService';
import { followCreator } from '../backend/mediaService';
import { CHAT_ENABLED } from '../config/featureFlags';
import { searchContent, SearchResult } from '../backend/searchService';

interface MobileAppProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  userProfile: UserProfile;
  onUpdateProfile: (updated: UserProfile) => void;
  mediaHistory: MediaHistoryItem[];
  onDeleteHistoryItem: (id: string) => void;
  onPublishMedia: (item: MediaHistoryItem) => void;
  currentUser?: AppUser | null;
  onOpenAuth: () => void;
  onLogout: () => void;
  onOpenPolicies: () => void;
  onOpenFeedback: () => void;
  onOpenAppSupport: () => void;
}

export const MobileApp: React.FC<MobileAppProps> = ({
  activeTab,
  onTabChange,
  userProfile,
  onUpdateProfile,
  mediaHistory,
  onDeleteHistoryItem,
  onPublishMedia,
  currentUser,
  onOpenAuth,
  onLogout,
  onOpenPolicies,
  onOpenFeedback,
  onOpenAppSupport
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(() => {
    return localStorage.getItem('clue200_permissions_agreed') !== 'true';
  });
  const [subscriptionToast, setSubscriptionToast] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notificationCounters, setNotificationCounters] = useState<NotificationCounters>({
    unreadNotifications: 0,
    lastReadAt: null
  });
  const [notificationCountersLoaded, setNotificationCountersLoaded] = useState(false);
  const [notificationCursor, setNotificationCursor] = useState<QueryDocumentSnapshot | null>(null);
  const [notificationsHaveMore, setNotificationsHaveMore] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushPromptError, setPushPromptError] = useState('');
  const [pushToast, setPushToast] = useState<{ title: string; body: string } | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<AppUser[]>([]);
  const [contentSearchResults, setContentSearchResults] = useState<SearchResult[]>([]);
  const [selectedProfileUid, setSelectedProfileUid] = useState<string | undefined>();
  const [publishedMedia, setPublishedMedia] = useState<MediaHistoryItem | undefined>();

  useEffect(() => {
    const term = globalSearch.trim();
    if (term.length >= 2) {
        const timer = setTimeout(() => {
            void searchUsers(term).then(setUserSearchResults);
            void searchContent(term).then(results => setContentSearchResults(results.filter(result => result.type !== 'channel')));
        }, 300);
        return () => clearTimeout(timer);
    } else {
        setUserSearchResults([]);
        setContentSearchResults([]);
    }
  }, [globalSearch]);

  const mediaSearchResults = globalSearch.trim().length >= 2
    ? (mediaHistory || [])
        .filter(item => {
          if (!item) return false;
          const term = globalSearch.trim().toLowerCase();
          const title = (item.title || '').toLowerCase();
          const type = (item.type || '').toLowerCase();
          const tags = (item.tags || []).join(' ').toLowerCase();
          return title.includes(term) || type.includes(term) || tags.includes(term);
        })
        .slice(0, 8)
    : [];

  useEffect(() => {
    setNotifications([]);
    setNotificationCounters({ unreadNotifications: 0, lastReadAt: null });
    setNotificationCountersLoaded(false);
    setNotificationCursor(null);
    setNotificationsHaveMore(false);
    if (!currentUser) return;
    return subscribeToNotificationCounters(currentUser.uid, counters => {
      setNotificationCounters(counters);
      setNotificationCountersLoaded(true);
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    let timer: number | undefined;
    const onPushReceived = (event: Event) => {
      const detail = (event as CustomEvent<{ title?: string; body?: string; notification?: { title?: string; body?: string } }>).detail;
      setPushToast({
        title: detail?.notification?.title || detail?.title || 'YeTryKar',
        body: detail?.notification?.body || detail?.body || 'You have a new notification.'
      });
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => setPushToast(null), 5000);
    };
    window.addEventListener('yetrykar:push-received', onPushReceived);
    return () => {
      window.removeEventListener('yetrykar:push-received', onPushReceived);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!showNotifications || !currentUser || !notificationCountersLoaded) return;
    let active = true;
    setNotificationsLoading(true);
    setNotificationsError('');
    void loadNotificationPage(currentUser.uid, null, notificationCounters.lastReadAt).then((page: NotificationPage) => {
      if (!active) return;
      setNotifications(page.items);
      setNotificationCursor(page.cursor);
      setNotificationsHaveMore(page.hasMore);
    }).catch(error => {
      if (active) setNotificationsError(error instanceof Error ? error.message : 'Could not load notifications.');
    }).finally(() => {
      if (active) setNotificationsLoading(false);
    });
    return () => { active = false; };
  }, [showNotifications, currentUser?.uid, notificationCountersLoaded]);

  useEffect(() => {
    const onInteraction = () => {
      if (!currentUser || localStorage.getItem('yetrykar_push_prompt_shown') === 'true') return;
      localStorage.setItem('yetrykar_push_prompt_shown', 'true');
      setShowPushPrompt(true);
    };
    window.addEventListener('yetrykar:notification-opt-in', onInteraction);
    return () => window.removeEventListener('yetrykar:notification-opt-in', onInteraction);
  }, [currentUser?.uid]);

  const unreadNotifications = notificationCounters.unreadNotifications;

  const loadMoreNotifications = async () => {
    if (!currentUser || !notificationCursor || notificationsLoading) return;
    setNotificationsLoading(true);
    setNotificationsError('');
    try {
      const page = await loadNotificationPage(currentUser.uid, notificationCursor, notificationCounters.lastReadAt);
      setNotifications(items => [...items, ...page.items]);
      setNotificationCursor(page.cursor);
      setNotificationsHaveMore(page.hasMore);
    } catch (error) {
      setNotificationsError(error instanceof Error ? error.message : 'Could not load more notifications.');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const enablePushFromPrompt = async () => {
    if (!currentUser) return;
    setPushPromptError('');
    try {
      await enablePushNotifications(currentUser.uid);
      const settings = await loadNotificationSettings(currentUser.uid);
      await saveNotificationSettings(currentUser.uid, { ...settings, pushEnabled: true });
      setShowPushPrompt(false);
    } catch (error) {
      setPushPromptError(error instanceof Error ? error.message : 'Could not enable push notifications.');
    }
  };

  const handleFollow = (creator: string, uid?: string) => {
    if (!currentUser || !uid) {
      onOpenAuth();
      return;
    }
    void followCreator(uid, creator, currentUser).then(followed => {
      if (!followed) {
        setSubscriptionToast('You are already following this creator.');
        return;
      }
      setSubscriptionToast(`Following ${creator}`);
    }).catch(error => {
      setSubscriptionToast(error instanceof Error ? error.message : 'Follow failed.');
    }).finally(() => {
      window.setTimeout(() => setSubscriptionToast(null), 2500);
    });
  };

  const handleTabRequest = (tab: TabType) => {
    console.log(`🎯 Tab requested: ${tab}`);
    if (tab === 'profile') {
        setSelectedProfileUid(undefined);
    }
    if (tab === 'create' && !currentUser) {
      onOpenAuth();
      return;
    }
    onTabChange(tab);
  };

  return (
    <div className="w-full max-w-[430px] min-h-screen sm:min-h-[860px] sm:my-4 sm:rounded-3xl sm:shadow-2xl sm:border sm:border-slate-200/80 bg-white overflow-hidden flex flex-col relative mx-auto">
      {/* Navigation Drawer */}
      <AppDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        currentPath={window.location.pathname}
        onNavigate={path => {
          window.history.pushState({}, '', path);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }}
      />

      {/* Mobile Top Navigation Header */}
      {activeTab !== 'shorts' && (
        <header className="sticky top-0 z-30 h-[var(--header-h)] shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-100 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              aria-label="Open Navigation Drawer"
              className="p-1 rounded-lg text-slate-700 hover:bg-slate-100 cursor-pointer"
            >
              <Menu className="w-5 h-5 text-slate-800" />
            </button>
            <BrandLogo size="md" />
            <label className="flex-1 max-w-[210px] relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={globalSearch}
                onChange={event => setGlobalSearch(event.target.value)}
                placeholder="Search people or videos"
                className="w-full pl-8 pr-2 py-1.5 rounded-lg bg-slate-100 text-[11px] outline-none"
                aria-label="Search people or videos"
              />
              {globalSearch.trim() && (
                <div className="absolute top-9 left-0 right-0 z-50 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden max-h-80 overflow-y-auto">
                  {/* User Results */}
                  {userSearchResults.length > 0 && (
                    <div className="border-b border-slate-100">
                        <p className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">People</p>
                        {userSearchResults.map(user => (
                            <div key={user.uid} className="w-full px-3 py-2.5 hover:bg-blue-50 flex items-center gap-2 transition-colors border-b border-slate-50 last:border-0">
                                <button
                                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                                    onClick={() => {
                                        setSelectedProfileUid(user.uid);
                                        onTabChange('profile');
                                        setGlobalSearch('');
                                    }}
                                >
                                    <img src={user.photoURL || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100'} className="w-8 h-8 rounded-full object-cover border border-slate-200" alt={user.displayName} />
                                    <div className="min-w-0 flex-1">
                                        <span className="block text-[11px] font-black text-slate-900 truncate uppercase tracking-tighter">{user.displayName}</span>
                                        <span className="block text-[9px] text-blue-600 font-bold tracking-tight">@{user.username}</span>
                                    </div>
                                </button>
                                <FollowButton
                                  targetUid={user.uid}
                                  targetName={user.displayName || user.username || 'creator'}
                                  currentUser={currentUser}
                                  onOpenAuth={onOpenAuth}
                                  compact
                                />
                            </div>
                        ))}
                    </div>
                  )}

                  {/* Media Results */}
                  {mediaSearchResults.length > 0 || contentSearchResults.length > 0 ? (
                    <div>
                        <p className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">Content</p>
                        {mediaSearchResults.map(item => (
                            <button
                            key={item.id}
                            className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-0 transition-colors"
                            onClick={() => {
                                setPublishedMedia(item); // Highlight for view
                                onTabChange(item.type === 'short' ? 'shorts' : item.type === 'long' || item.type === 'video' || item.type === 'live' ? 'longs' : 'home');
                                setGlobalSearch('');
                            }}
                            >
                                <span className="block text-[11px] font-bold text-slate-800 truncate">{item.title || 'Untitled'}</span>
                                <span className="block text-[9px] uppercase text-slate-400 font-black tracking-widest">{item.type}</span>
                            </button>
                        ))}
                        {contentSearchResults.map(result => (
                          <button
                            key={`${result.type}-${result.id}`}
                            className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-0 transition-colors"
                            onClick={() => {
                              onTabChange(result.type === 'short' ? 'shorts' : result.type === 'long' ? 'longs' : 'home');
                              setGlobalSearch('');
                            }}
                          >
                            <span className="block text-[11px] font-bold text-slate-800 truncate">{result.title}</span>
                            <span className="block text-[9px] uppercase text-slate-400 font-black tracking-widest">{result.type} · {result.subtitle}</span>
                          </button>
                        ))}
                    </div>
                  ) : (
                    userSearchResults.length === 0 && <p className="px-3 py-4 text-[10px] text-slate-500 text-center font-medium">No results found for "{globalSearch}"</p>
                  )}
                </div>
              )}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                aria-label="Notifications"
                onClick={() => {
                  if (!currentUser) {
                    onOpenAuth();
                    return;
                  }
                  setShowNotifications(value => !value);
                }}
                className="relative p-1.5 text-slate-500 hover:text-blue-900"
              >
                <Bell className="w-4 h-4" />
                {unreadNotifications > 0 && (
                  <span className="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full bg-rose-600 text-white text-[9px] font-black flex items-center justify-center">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-9 z-50 w-72 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                  <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100">
                    <div>
                      <strong className="text-xs block">Notifications</strong>
                      <span className="text-[9px] text-slate-500">Unread: {unreadNotifications}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        aria-label="Notification settings"
                        title="Notification settings"
                        onClick={() => setShowNotificationSettings(true)}
                        className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                      >
                        <Settings size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (!currentUser) return;
                          void markAllNotificationsRead(currentUser.uid).then(() => {
                            setNotificationCounters({ unreadNotifications: 0, lastReadAt: Timestamp.now() });
                            setNotifications(items => items.map(item => ({ ...item, unread: false })));
                          }).catch(error => setNotificationsError(error instanceof Error ? error.message : 'Could not mark notifications read.'));
                        }}
                        disabled={unreadNotifications === 0}
                        className="text-[10px] font-bold text-blue-700 disabled:text-slate-400"
                      >
                        Mark all read
                      </button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notificationsError && <p role="alert" className="p-3 text-[10px] text-rose-700">{notificationsError}</p>}
                    {notificationsLoading && notifications.length === 0 && <p className="p-4 text-center text-[11px] text-slate-500">Loading…</p>}
                    {!notificationsLoading && notifications.length === 0 && !notificationsError && <p className="p-4 text-[11px] text-slate-500">No notifications yet.</p>}
                    {notifications.map(item => (
                      <div key={item.id} className={`px-3 py-2 border-b border-slate-50 text-[11px] ${item.unread ? 'font-bold text-slate-900 bg-blue-50/40' : 'text-slate-500'}`}>
                        <span className="block">{item.count > 1 ? `${item.actorName} and ${item.count - 1} others` : item.actorName} {item.preview}</span>
                        <span className="text-[9px] text-slate-400">{new Date(item.createdAt).toLocaleString()}</span>
                      </div>
                    ))}
                    {notificationsHaveMore && (
                      <button type="button" disabled={notificationsLoading} onClick={() => void loadMoreNotifications()} className="w-full p-2 text-[10px] font-bold text-blue-800 disabled:opacity-50">
                        {notificationsLoading ? 'Loading…' : 'Load more'}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      onOpenPolicies();
                    }}
                    className="w-full border-t border-slate-100 px-3 py-2 text-left text-[10px] font-bold text-slate-600 hover:bg-slate-50"
                  >
                    Policies
                  </button>
                </div>
              )}
            </div>
            {currentUser ? (
              <div className="relative">
                <button
                  id="mobile-header-profile-btn"
                  onClick={() => {
                    setShowUserMenu(value => !value);
                    setSelectedProfileUid(undefined);
                  }}
                  className="w-7 h-7 rounded-full overflow-hidden border border-slate-200 cursor-pointer shadow-2xs hover:ring-2 hover:ring-blue-900/20 transition-all"
                  title={`Signed in as ${currentUser.displayName}`}
                >
                  <img
                    src={currentUser.photoURL || userProfile.avatar}
                    alt={currentUser.displayName}
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                  />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-10 z-50 w-40 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setSelectedProfileUid(undefined);
                        onTabChange('profile');
                      }}
                      className="w-full text-left px-3 py-2.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                    >
                      My Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowUserMenu(false);
                        onOpenFeedback();
                      }}
                      className="w-full border-t border-slate-100 px-3 py-2.5 text-left text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                    >
                      Feedback
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowUserMenu(false);
                        onOpenAppSupport();
                      }}
                      className="w-full border-t border-slate-100 px-3 py-2.5 text-left text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                    >
                      App Support
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        void onLogout();
                      }}
                      className="w-full text-left px-3 py-2.5 text-[11px] font-bold text-rose-600 hover:bg-rose-50 border-t border-slate-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                id="mobile-header-login-btn"
                onClick={onOpenAuth}
                className="flex items-center gap-1 px-2 py-1 bg-blue-900 hover:bg-blue-950 text-white rounded-lg text-[11px] font-semibold cursor-pointer shadow-2xs transition-colors"
              >
                <LogIn className="w-3 h-3" />
                <span>Sign In</span>
              </button>
            )}
          </div>
        </header>
      )}

      {showPushPrompt && currentUser && (
        <aside className="absolute left-3 right-3 top-16 z-40 rounded-2xl border border-blue-100 bg-white p-3 shadow-lg" aria-label="Notification opt-in">
          <p className="text-xs font-bold text-slate-900">Get notified when someone follows or replies?</p>
          <p className="mt-1 text-[10px] text-slate-500">You can change notification types and quiet hours in settings.</p>
          {pushPromptError && <p role="alert" className="mt-2 text-[10px] text-rose-700">{pushPromptError}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setShowPushPrompt(false)} className="rounded-lg px-3 py-1.5 text-[10px] font-bold text-slate-500">Not now</button>
            <button type="button" onClick={() => void enablePushFromPrompt()} className="rounded-lg bg-blue-900 px-3 py-1.5 text-[10px] font-bold text-white">Enable notifications</button>
          </div>
        </aside>
      )}
      {pushToast && (
        <aside className="absolute left-3 right-3 top-32 z-40 rounded-xl bg-slate-900 px-3 py-2 text-white shadow-lg" role="status">
          <p className="text-xs font-bold">{pushToast.title}</p>
          <p className="mt-0.5 text-[10px] text-slate-200">{pushToast.body}</p>
        </aside>
      )}
      {showNotificationSettings && currentUser && (
        <Suspense fallback={<div className="p-4 text-center text-xs text-slate-500">Loading settings...</div>}>
          <NotificationSettingsModal uid={currentUser.uid} onClose={() => setShowNotificationSettings(false)} />
        </Suspense>
      )}

      {/* Main Screen Body Viewport */}
      <main data-screen-scroll-root className="flex-1 overflow-y-auto no-scrollbar relative bg-white pb-[var(--nav-h)] min-h-0">
        <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading view...</div>}>
          {activeTab === 'home' && (
            <HomeTab
              mediaHistory={mediaHistory}
              onTabChange={onTabChange}
              currentUser={currentUser}
              highlightedMedia={publishedMedia}
              onOpenAuth={onOpenAuth}
              onOpenSubscribeModal={handleFollow}
              onOpenProfile={uid => { console.log('👤 Navigating to profile:', uid); setSelectedProfileUid(uid); onTabChange('profile'); }}
            />
          )}
          {activeTab === 'shorts' && (
            <ShortsTab
              mediaHistory={mediaHistory}
              onOpenAuth={onOpenAuth}
              currentUser={currentUser}
              highlightedMedia={publishedMedia}
              onOpenProfile={uid => { console.log('👤 Navigating to profile from shorts:', uid); setSelectedProfileUid(uid); onTabChange('profile'); }}
            />
          )}
          {activeTab === 'longs' && (
            <LongsTab
              mediaHistory={mediaHistory}
              currentUser={currentUser}
              onOpenAuth={onOpenAuth}
              onGoLive={() => onTabChange('live')}
              highlightedMedia={publishedMedia}
              onOpenProfile={uid => { console.log('👤 Navigating to profile from longs:', uid); setSelectedProfileUid(uid); onTabChange('profile'); }}
            />
          )}
          {activeTab === 'live' && (
            <LiveStreamScreen
              currentUser={currentUser}
              onClose={() => onTabChange('home')}
              onOpenAuth={onOpenAuth}
              onOpenSubscribeModal={handleFollow}
            />
          )}
          {activeTab === 'create' && (
            <CreateModal
              preserveBottomNavigation
              currentUser={currentUser}
              onOpenAuth={onOpenAuth}
              onGoLive={() => onTabChange('live')}
              onPublish={item => {
                 setPublishedMedia(item);
                 onPublishMedia(item);
                if (item.type === 'short') {
                  onTabChange('shorts');
                } else if (item.type === 'video' || item.type === 'long') {
                  onTabChange('longs');
                } else {
                  onTabChange('home');
                }
              }}
            />
          )}
          {activeTab === 'profile' && (
            <ProfileTab
              profile={userProfile}
              onUpdateProfile={onUpdateProfile}
              mediaHistory={mediaHistory}
              onDeleteHistoryItem={onDeleteHistoryItem}
              currentUser={currentUser}
              onLogout={onLogout}
              onOpenAuth={onOpenAuth}
              viewingUserId={selectedProfileUid}
              onOpenSubscribeModal={handleFollow}
              onOpenProfile={uid => { setSelectedProfileUid(uid); onTabChange('profile'); }}
              onOpenUpload={() => onTabChange('create')}
              onOpenFeedback={onOpenFeedback}
              onOpenAppSupport={onOpenAppSupport}
            />
          )}

          {activeTab === 'chat' && currentUser && (
            <ChatScreen currentUser={currentUser} onClose={() => onTabChange('profile')} />
          )}
        </Suspense>
      </main>

      {/* Mandatory Device Permissions and Terms Modal */}
      <Suspense fallback={null}>
        <AppPermissionsModal
          isOpen={showPermissionsModal}
          onAgreeAndContinue={() => {
            localStorage.setItem('clue200_permissions_agreed', 'true');
            setShowPermissionsModal(false);
          }}
        />
      </Suspense>

      {subscriptionToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[120] bg-emerald-600 text-white px-4 py-3 rounded-2xl shadow-xl text-xs font-bold">
          {subscriptionToast}
        </div>
      )}

      {/* Mobile Bottom 5-Tabs Navigation Bar (Clean Minimalism) */}
      <nav className="fixed sm:absolute bottom-0 left-0 right-0 z-[1100] bg-white/95 backdrop-blur-md border-t border-slate-100 px-2 h-[var(--nav-h)] flex items-center justify-around select-none">
        {/* 1. Home Tab */}
        <button
          id="mobile-tab-home"
          onClick={() => handleTabRequest('home')}
          className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all cursor-pointer ${
            activeTab === 'home'
              ? 'text-blue-900 font-semibold'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          <Home className={`w-4 h-4 ${activeTab === 'home' ? 'stroke-[2.5px]' : 'stroke-2'}`} />
          <span className="text-[10px]">Home</span>
        </button>
        {CHAT_ENABLED && (
          <button
            id="mobile-tab-chat"
            onClick={() => handleTabRequest('chat')}
            className={`flex flex-col items-center gap-0.5 py-1 px-2 rounded-xl transition-all cursor-pointer min-w-[48px] ${
              activeTab === 'chat' ? 'text-blue-900 font-semibold' : 'text-slate-400 hover:text-slate-700'
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-[10px]">Chat</span>
          </button>
        )}

        {/* 2. MiniS Tab */}
        <button
          id="mobile-tab-shorts"
          onClick={() => handleTabRequest('shorts')}
          className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all cursor-pointer ${
            activeTab === 'shorts'
              ? 'text-blue-900 font-semibold'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          <Film className={`w-4 h-4 ${activeTab === 'shorts' ? 'stroke-[2.5px]' : 'stroke-2'}`} />
          <span className="text-[10px]">MiniS</span>
        </button>

        {/* 3. Create+ Tab (Special Highlighted Center Button) */}
        <button
          id="mobile-tab-create"
          onClick={() => handleTabRequest('create')}
          className="flex flex-col items-center justify-center -mt-2 cursor-pointer transition-transform active:scale-95"
          title="Create New Media"
        >
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md transition-all ${
              activeTab === 'create'
                ? 'bg-blue-900 ring-2 ring-blue-900/30'
                : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            <Plus className="w-5 h-5 stroke-[2.5px]" />
          </div>
          <span className="text-[9px] font-semibold text-slate-600 mt-0.5">Create</span>
        </button>

        {/* 4. Longs Tab */}
        <button
          id="mobile-tab-longs"
          onClick={() => handleTabRequest('longs')}
          className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all cursor-pointer ${
            activeTab === 'longs'
              ? 'text-blue-900 font-semibold'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          <PlaySquare className={`w-4 h-4 ${activeTab === 'longs' ? 'stroke-[2.5px]' : 'stroke-2'}`} />
          <span className="text-[10px]">Longs</span>
        </button>

        {/* 5. Profile Tab */}
        <button
          id="mobile-tab-profile"
          onClick={() => handleTabRequest('profile')}
          className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all cursor-pointer ${
            activeTab === 'profile'
              ? 'text-blue-900 font-semibold'
              : 'text-slate-400 hover:text-slate-700'
          }`}
        >
          {currentUser ? (
            <div
              className={`w-4 h-4 rounded-full overflow-hidden border ${
                activeTab === 'profile' ? 'border-blue-900 ring-1 ring-blue-900' : 'border-slate-300'
              }`}
            >
              <img
                src={currentUser.photoURL || userProfile.avatar}
                alt="Avatar"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <User className={`w-4 h-4 ${activeTab === 'profile' ? 'stroke-[2.5px]' : 'stroke-2'}`} />
          )}
          <span className="text-[10px]">Profile</span>
        </button>
      </nav>
    </div>
  );
};
